import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import { LaerdalLogo, LaerdalWhiteLogo } from '../assets';
import {BREAKPOINTS, COLORS, ComponentTextStyle, focusStyles, invertedFocusStyles} from '../styles';
import { ComponentMStyling, ComponentXSStyling } from '../styles';
import {defaultOnMouseDownHandler} from '../common';
import { StyledLink } from '../HyperLink/HyperLink';

const CustomizedLink = styled(props => <StyledLink {...props} />)<{ $color?: string }>`
  text-align: center;
  text-decoration: none;
  display: flex;
  align-items: center;
  &:focus-within {
    outline: none;
  }
  svg {
    width: 66px;
    height: 36px;
    color: ${(props) => props.$color};
    ${BREAKPOINTS.MEDIUM} {
      width: 88px;
      height: 48px;
    }
  }
`;

const LogoContainer = styled.div`
  display: flex;
  max-width: calc(100% - 48px);
  overflow: hidden;
  align-items: center;
  height: 40px;
  box-sizing: border-box;
  padding: 24px 0;

  ${BREAKPOINTS.LARGE} {
    margin-top: 8px;
    margin-bottom: 7px;
  }

  &:focus-within {
    ${focusStyles}
  }

  &.inverted:focus-within {
    ${invertedFocusStyles}
  }
`;

const Name = styled.span<{ $color?: string; $noSizeChangeOnMobile: boolean }>`
  margin-left: 8px;
  padding-left: 8px;
  border-left: 1px solid ${(props) => props.$color || COLORS.getColor('black', props.theme)};
  letter-spacing: -0.02em;
  max-width: 200px;
  
  white-space: nowrap;
  overflow: hidden;

  ${(props) =>
    props.$noSizeChangeOnMobile
      ? ComponentMStyling(ComponentTextStyle.Regular, props.$color || COLORS.getColor('black', props.theme))
      : ComponentXSStyling(ComponentTextStyle.Regular, props.$color || COLORS.getColor('black', props.theme))}

  font-weight: 300 !important;
  line-height: 24px !important;

  ${BREAKPOINTS.MEDIUM} {
    ${(props) => ComponentMStyling(ComponentTextStyle.Regular, props.$color || COLORS.getColor('black', props.theme))};
  }

  ${BREAKPOINTS.LARGE} {
    margin-left: 12px;
    padding-left: 12px;
  }
`;

const BetaTag = styled.span<{ $backgroundColor?: string; $color?: string }>`
  background-color: ${(props) => props.$backgroundColor || COLORS.getColor('accent2_500', props.theme)};
  padding: 4px 8px;
  margin-left: 8px;
  margin-top: 2px;
  border-left: none;
  border-radius: 2px;

  ${(props) => ComponentXSStyling(ComponentTextStyle.UppercaseBold, props.$color || COLORS.getColor('white', props.theme))}
`;

type LogoProps = {
  name: string;
  showBetaTag?: boolean;
  onClick?: () => void;
  to?: string;
  color?: string;
  noSizeChangeOnMobile?: boolean;
  betaTagBackgroundColor?: string;
  betaTagColor?: string;
  inverted?: boolean;
};

const Logo: React.FunctionComponent<LogoProps> = ({ name, showBetaTag, onClick, to, color, noSizeChangeOnMobile, betaTagBackgroundColor, betaTagColor, inverted }: LogoProps) => {
  const theme = useTheme();
  return (
    <LogoContainer className={inverted ? 'inverted' : ''}>
      <CustomizedLink
        target='_self'
        $variant='styleless'
        href={to || '/'}
        onMouseDown={defaultOnMouseDownHandler}
        onClick={(e: any) => {
          if (onClick) {
            onClick();
            e.preventDefault();
          }
        }}
        $color={color}>
        {color === COLORS.getColor('white', theme) ? <LaerdalWhiteLogo /> : <LaerdalLogo />}
        <Name $color={color} $noSizeChangeOnMobile={noSizeChangeOnMobile || false}>
          {name}
        </Name>
        {showBetaTag && (
          <BetaTag $backgroundColor={betaTagBackgroundColor} $color={betaTagColor}>
            BETA
          </BetaTag>
        )}
      </CustomizedLink>
    </LogoContainer>
  );
};

export default Logo;
